import { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../../components/ui";
import type { EditorAsset } from "../editor.types";
import { BottomSheet } from "./BottomSheet";
import { MediaGrid } from "./MediaGrid";

/**
 * Choose from the site's images (or upload more): one, picked with a tap,
 * or several, ticked and then added together.
 */
export function MediaPicker({
  assets,
  multiple = false,
  title = "Your images",
  onUpload,
  onChoose,
  onClose,
}: {
  assets: EditorAsset[];
  multiple?: boolean;
  title?: string;
  /** Uploads a file to the site's images and returns it. */
  onUpload: (file: File) => Promise<EditorAsset>;
  onChoose: (chosen: EditorAsset[]) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<EditorAsset[]>([]);
  // Uploads arrive in the library at once (picked, when choosing several).
  const [added, setAdded] = useState<EditorAsset[]>([]);
  const all = [...added, ...assets.filter((asset) => !added.some((a) => a.id === asset.id))];
  const pick = (asset: EditorAsset) => {
    if (!multiple) return onChoose([asset]);
    setPicked((now) => (now.some((a) => a.id === asset.id) ? now.filter((a) => a.id !== asset.id) : [...now, asset]));
  };
  return createPortal(
    <BottomSheet open title={title} titleId="media-picker-title" onClose={onClose}>
      <div className="-mx-1 min-h-0 overflow-y-auto px-1 pt-1 pb-2">
        <MediaGrid
          label={title}
          assets={all}
          selected={picked.map((asset) => asset.id)}
          onPick={pick}
          onUpload={async (files) => {
            const uploaded: EditorAsset[] = [];
            for (const file of files) uploaded.push(await onUpload(file));
            setAdded((now) => [...uploaded, ...now]);
            if (!multiple && uploaded.length === 1) onChoose(uploaded);
            else setPicked((now) => [...now, ...uploaded]);
          }}
        />
      </div>
      {multiple && (
        <div className="mt-3 flex shrink-0 items-center justify-end gap-2 border-t border-hazumu-border pt-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" disabled={!picked.length} onClick={() => onChoose(picked)}>
            {picked.length > 1 ? `Add ${picked.length} images` : "Add image"}
          </Button>
        </div>
      )}
    </BottomSheet>,
    document.body,
  );
}
